<script setup lang="ts">
import { customTabsCollections } from '~/utils/customTabsConfigs';
import top10newgames from '~/assets/json/top10newgames.json';

const casino = useCasinoStore();

const { userCanOnlyWithdraw } = useUserLimits();
const { isWithinMigrationPeriod } = useFeatureFlagsNew();
const { isDesktop, isMobile } = useLayout();

const { resetTracking } = useAboveFoldTracking();
const {
  belowFoldCanLoad,
  belowFoldLowPriorityCanLoad,
  initializePriorityLoading,
} = usePriorityLoading();

// Refs para cada seção below-the-fold
const vipTabsRef = ref<HTMLElement | null>(null);
const provedorasRef = ref<HTMLElement | null>(null);
const perfisRef = ref<HTMLElement | null>(null);
const crashRef = ref<HTMLElement | null>(null);
const casinoRowsRef = ref<HTMLElement | null>(null);

// Estados de visibilidade - desktop: true, mobile: controlado por scroll
const vipTabsVisible = ref(false);
const provedorasVisible = ref(false);
const perfisVisible = ref(false);
const crashVisible = ref(false);
const casinoRowsVisible = ref(false);

// Gerenciador de observers para cleanup
const observers: IntersectionObserver[] = [];

// Cria um observer para uma seção específica
function createSectionObserver(
  elRef: Ref<HTMLElement | null>,
  visibleRef: Ref<boolean>,
) {
  watch(
    elRef,
    (el) => {
      if (!el || !isMobile.value || visibleRef.value) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          if (entry?.isIntersecting) {
            visibleRef.value = true;
            observer.disconnect();
          }
        },
        { rootMargin: '600px' },
      );

      observer.observe(el);
      observers.push(observer);
    },
    { immediate: true },
  );
}

const filteredCasinoRows = computed(() => {
  return casino.rows.filter((gamesRows: FinalRow) =>
    gamesRows?.icon?.includes('provedoras'),
  );
});

onMounted(() => {
  // Reset tracking quando a Home monta
  resetTracking();

  // Inicializa sistema de carregamento progressivo
  initializePriorityLoading();

  // Em mobile, adia carregamento de jogos até casino rows estar visível
  if (isMobile.value) {
    watch(
      casinoRowsVisible,
      (canLoad) => {
        if (canLoad) {
          casino.getFilteredHomeGames(1);
        }
      },
      { immediate: true },
    );
  } else {
    // Desktop: carrega tudo imediatamente
    casino.getFilteredHomeGames(1);
    vipTabsVisible.value = true;
    provedorasVisible.value = true;
    perfisVisible.value = true;
    crashVisible.value = true;
    casinoRowsVisible.value = true;
  }
});

// Configura observers para cada seção (mobile only)
createSectionObserver(vipTabsRef, vipTabsVisible);
createSectionObserver(provedorasRef, provedorasVisible);
createSectionObserver(perfisRef, perfisVisible);
createSectionObserver(crashRef, crashVisible);
createSectionObserver(casinoRowsRef, casinoRowsVisible);

onUnmounted(() => {
  observers.forEach((obs) => obs.disconnect());
});

function formatTitleClass(title: string): string {
  return (
    'title-' +
    title
      .toLowerCase()
      .replaceAll(/\s+/g, '-') // espaços viram hífen
      .replaceAll(/[^a-z0-9]/g, '')
  ); // remove caracteres especiais
}
</script>

<template>
  <UserAccountProtectionAlertOnlyWithdraw />

  <WidgetsMigrationHomeMessage v-if="isWithinMigrationPeriod" />

  <div
    v-else-if="!userCanOnlyWithdraw"
    :class="$style.homePage"
  >
    <SectionsMenuHorizontalApostatudo v-if="isDesktop" />

    <SectionsSliderApostatudo v-if="isDesktop" />

    <BaseSearchContainer />

    <!-- <CasinoFavoriteGamesList /> -->

    <CasinoLastGamesPlayed />

    <!-- ACIMA DO FOLD: Queridinhos, Information Banner, Novidades -->
    <SectionsTabsApostatudo
      header-icon-path="home/preferidos"
      header-see-more-url="/games/category/all?initialCategory=selecao-apostatudo"
      header-title="Queridinhos"
      :is-tab-visible="false"
      loading-strategy="eager"
      section-slug="selecao-apostatudo"
      :tabs="customTabsCollections.colecao1"
    />

    <!-- Information Banner: sempre acima do fold em mobile -->
    <SectionsInformationBanners v-if="!isDesktop" />

    <!-- Novidades: sempre acima do fold -->
    <PerfilOficialMediaList
      :button="{ url: '/games/category/jogos-novos', text: 'VER MAIS' }"
      class="mb-4"
      disable-lazy-loading
      display-mode="slide"
      icon-path="home/mais-jogados"
      :items="top10newgames"
      loading-numbers="eager"
      :mobile-eager-count="3"
      :per-row="{ 500: 3.3, 990: 5.1, desktop: 6 }"
      see-more-url="/games/category/all?initialCategory=jogos-novos"
      :show-numbers="true"
      :space-between="[22, 16, 10]"
      title="Novidades"
    />

    <!-- ABAIXO DO FOLD: Slots e demais seções -->
    <SectionsTabsApostatudo
      header-icon-path="home/slots"
      header-see-more-url="/games/category/all?initialCategory=slots"
      header-title="Slots"
      :is-tab-visible="true"
      loading-strategy="eager"
      :tabs="customTabsCollections.colecao2"
    />

    <!-- Below-the-fold content - carrega progressivamente em mobile -->
    <template v-if="!isMobile || belowFoldCanLoad">
      <!-- Jogos ao Vivo -->
      <div ref="provedorasRef">
        <LazySectionsTabsApostatudo
          v-if="provedorasVisible"
          header-icon-path="home/slots"
          header-see-more-url="/games/category/all"
          header-title="Jogos ao Vivo"
          :tabs="customTabsCollections.colecao3"
        />
      </div>

      <!-- VIP Tabs: Missões, Torneios, Minigames, Loja -->
      <div ref="vipTabsRef">
        <LazySectionsTabsVIP
          v-if="vipTabsVisible"
          header-icon-path="home/tournament"
          header-see-more-url="/vip"
          header-title="Área VIP"
        />
      </div>

      <!-- Perfis Oficiais: carrega apenas quando scrollar até aqui -->
      <div ref="perfisRef">
        <LazySectionsPerfilOficialList
          v-if="perfisVisible"
          :button="{ to: '/perfil-oficial', text: 'VER MAIS' }"
          class="mb-4"
          display-mode="slide"
          icon="home/mais-jogados"
          load-from-config
          :per-row="[6, 5, 3]"
          :space-between="[22, 16, 10]"
          title="Perfis Oficiais"
        />
      </div>

      <!-- Crash: carrega apenas quando scrollar até aqui -->
      <div ref="crashRef">
        <LazySectionsTabsApostatudo
          v-if="crashVisible"
          header-icon-path="home/ganhos-nas-alturas"
          header-see-more-url="/games/category/jogos-crash"
          header-title="Crash"
          :is-tab-visible="false"
          :tabs="customTabsCollections.colecao4"
        />
      </div>
    </template>

    <!-- Casino rows - baixa prioridade em mobile -->
    <template v-if="!isMobile || belowFoldLowPriorityCanLoad">
      <div ref="casinoRowsRef">
        <template v-if="casinoRowsVisible">
          <template v-if="!casino.hasError">
            <template v-if="!casino.rows.length">
              <template
                v-for="i in 5"
                :key="i"
              >
                <LazyCasinoMediaList
                  class="mb-4"
                  display-mode="slide"
                  :is-loading="true"
                  :per-row="{ 768: 3, 1100: 4, 1200: 5, desktop: 6 }"
                  :space-between="[22, 16, 10]"
                />
              </template>
            </template>

            <template
              v-for="(gamesRows, i) in filteredCasinoRows"
              :key="i"
            >
              <LazyCasinoMediaList
                :button="gamesRows.button"
                :class="['mb-4', formatTitleClass(gamesRows.title)]"
                display-mode="slide"
                :icon="gamesRows.icon"
                :is-loading="casino.isLoading"
                :items="gamesRows.child"
                :per-row="{ 768: 3, 1100: 4, 1200: 5, desktop: 6 }"
                :space-between="[22, 16, 10]"
                :title="$t(`${gamesRows.title}`) || gamesRows.title"
                :type="gamesRows.sliderType"
              />
            </template>
          </template>

          <template v-else>
            <h3>Erro ao buscar jogos</h3>
          </template>
        </template>
      </div>
    </template>
  </div>
</template>

<style module lang="postcss">
.homePage {
  @apply pb-2 sm:pb-4 -mt-4 lg:mt-4 xl:mt-[1.4rem];
}
</style>
